import React from 'react';

/**
 * Hjezly Luxury Logo Component
 * Premium black & gold branding with elegant Arabic-inspired design
 * 
 * Props:
 * - size: 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * - variant: 'full' | 'icon' | 'text' (default: 'full')
 * - className: additional CSS classes
 */

const HjezlyLogo = ({ size = 'md', variant = 'full', className = '' }) => {
  const sizes = {
    sm: { fontSize: 'text-xl', iconSize: 32 },
    md: { fontSize: 'text-2xl', iconSize: 40 },
    lg: { fontSize: 'text-3xl', iconSize: 48 },
    xl: { fontSize: 'text-4xl', iconSize: 56 }
  };

  const { fontSize, iconSize } = sizes[size];

  // Icon/Emblem only (Star with H inside)
  const IconOnly = () => (
    <svg 
      width={iconSize} 
      height={iconSize} 
      viewBox="0 0 100 100" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Outer Gold Ring */}
      <circle cx="50" cy="50" r="48" stroke="url(#goldGradient)" strokeWidth="2" fill="none" />
      
      {/* Inner Star Pattern */}
      <path 
        d="M50 10 L55 40 L85 40 L60 58 L70 88 L50 70 L30 88 L40 58 L15 40 L45 40 Z" 
        fill="url(#goldGradient)" 
        opacity="0.3"
      />
      
      {/* Center Circle Background */}
      <circle cx="50" cy="50" r="32" fill="url(#blackGradient)" />
      
      {/* Stylized 'H' Letter */}
      <path 
        d="M35 35 L35 65 M35 50 L65 50 M65 35 L65 65" 
        stroke="url(#goldGradient)" 
        strokeWidth="4" 
        strokeLinecap="round"
      />
      
      {/* Small Stars Decoration */}
      <circle cx="30" cy="30" r="2" fill="url(#goldGradient)" opacity="0.8" />
      <circle cx="70" cy="30" r="2" fill="url(#goldGradient)" opacity="0.8" />
      <circle cx="30" cy="70" r="2" fill="url(#goldGradient)" opacity="0.6" />
      <circle cx="70" cy="70" r="2" fill="url(#goldGradient)" opacity="0.6" />
      
      {/* Gradients */}
      <defs>
        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD700" />
          <stop offset="50%" stopColor="#FDB931" />
          <stop offset="100%" stopColor="#C9A227" />
        </linearGradient>
        <radialGradient id="blackGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
      </defs>
    </svg>
  );

  // Text only (Hjezly wordmark)
  const TextOnly = () => (
    <div className={`font-bold ${fontSize} ${className}`}>
      <span className="bg-gradient-to-r from-[#FFD700] via-[#FDB931] to-[#C9A227] bg-clip-text text-transparent">
        Hjezly
      </span>
    </div>
  );

  // Full logo (Icon + Text)
  const FullLogo = () => (
    <div className={`flex items-center gap-3 ${className}`}>
      <IconOnly />
      <div className="flex flex-col">
        <span className={`font-bold ${fontSize} leading-none bg-gradient-to-r from-[#FFD700] via-[#FDB931] to-[#C9A227] bg-clip-text text-transparent`}>
          Hjezly
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-400 dark:text-neutral-500 mt-0.5">
          Premium Venues
        </span>
      </div>
    </div>
  );

  if (variant === 'icon') return <IconOnly />;
  if (variant === 'text') return <TextOnly />;
  return <FullLogo />;
};

export default HjezlyLogo;
